// tslint:disable: no-magic-numbers max-file-line-count
import { _throw } from 'rxjs/observable/throw';
import { catchError, concatMap, tap, finalize } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { get as getValue } from 'lodash';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { empty } from 'rxjs/observable/empty';

import * as momentModule from 'moment';

import { AgencyAuthToken } from './interfaces/agency-auth-token.interface';
import { AgencyLogin } from './interfaces/agency-login.interface';
import { AgencyLogout } from './interfaces/agency-logout.interface';
import { AgencyLookup } from './interfaces/agency-lookup.interface';
import { AgencyPassport } from './interfaces/agency-passport.interface';
import { AgencyPassportRequest } from './interfaces/agency-passport-request.interface';
import { GuestCastawayInfo } from '../interfaces/guest-castaway-info.interface';
import { TRAVEL_AGENT_CONSTANTS } from './travel-agent.constants';
import { TravelAgentRestService } from './rest/travel-agent-rest.service';
import { WindowRef } from '../window-ref/window-ref.service';

@Injectable()
export class TravelAgentService {
    // TODO: flowEnabled property should be removed when travel agent toggle removed (enabled as default)
    private flowEnabled: boolean = false;
    private constants = TRAVEL_AGENT_CONSTANTS;
    private moment = momentModule;
    private sessionExpirationDate: Date;
    private oneDayExpirationDate: Date;

    constructor(
        private cookieService: CookieService,
        private travelAgentRestService: TravelAgentRestService,
        private windowRef: WindowRef
    ) {}

    /**
     * Set if travel agent flow is enabled based on toggle value
     * @param toggleValue travel agent feature toggle value
     */
    setFlowEnabled(toggleValue: boolean): void {
        this.flowEnabled = toggleValue;
    }

    /**
     * Set expiration times variables for session related and TA related cookies
     * @param expiresIn auth token expiration time
     */
    setCookiesExpirationTimes(expiresIn: string): void {
        this.sessionExpirationDate = this.moment().add(
            parseInt(expiresIn, 10) || this.constants.SESSION_HOUR_SEC,
            'seconds'
        ).toDate();
        this.oneDayExpirationDate = this.moment().add(
            this.constants.DEFAULT_DAY_EXPIRATION_MIN,
            'minutes'
        ).toDate();
    }

    /**
     * Set webapi base url for rest service
     * @param baseUrl
     */
    setRestBaseUrl(baseUrl: string): void {
        this.travelAgentRestService.setBaseUrl(baseUrl);
    }

    /**
     * Validate if a Travel Agent user is logged in
     * @returns Validation result
     */
    isTravelAgentLoggedIn(): boolean {
        return this.flowEnabled && !!this.cookieService.get(this.constants.COOKIES.TA_INFO);
    }

    /**
     * Returns agency id for a logged in Travel Agent user
     * @returns Agency id value
     */
    getTravelAgencyId(): string {
        return this.cookieService.get(this.constants.COOKIES.AGENCY_ID);
    }

    /**
     * Returns auth token for a logged in Travel Agent user, to be used in Authorization header
     * @returns Value for Authorization header
     */
    getTravelAgentAuthToken(): string {
        return this.travelAgentRestService.getAuthHeader();
    }

    /**
     * Returns AgencyUUID based on agencyId and Conversation_UUID cookies
     * @returns AgencyUUID value for the session
     */
    getAgencyUUID(): string {
        const agencyId = this.getTravelAgencyId();
        const conversationUUID = this.cookieService.get(this.constants.COOKIES.CONVERSATION_UUID);

        return agencyId && conversationUUID ? `${conversationUUID}_${agencyId}` : '';
    }

    /**
     * Removes a Travel Agent related cookie with
     * regular domain
     * starting with dot domain
     * base level domain. Example: go.com, disney.go.com
     * @param cookieName
     */
    removeCookie(cookieName: string): void {
        const { path, domain, secondLevelDomain, thirdLevelDomain } = this.constants.COOKIES.COOKIE_OPTIONS;

        this.cookieService.delete(cookieName, path);
        this.cookieService.delete(cookieName, path, domain);
        this.cookieService.delete(cookieName, path, secondLevelDomain);
        this.cookieService.delete(cookieName, path, thirdLevelDomain);
    }

    /**
     * Stores auth token based cookies
     * @param data auth token response
     * @param { string } data.access_token auth token (B2B)
     * @param { string } data.expires_in auth token expiration time
     */
    storeAuthToken({ access_token, expires_in }: AgencyAuthToken): void {
        const cookieConstants = this.constants.COOKIES;
        const storageConstants = this.constants.STORAGE;
        const { path, domain } = cookieConstants.COOKIE_OPTIONS;
        const expires = this.moment().add(this.constants.SESSION_MIN, 'minutes').toDate();
        const ttlString = String(expires.getTime());
        const currentTime = String(new Date().valueOf());

        this.setCookiesExpirationTimes(expires_in);

        this.removeCookie(cookieConstants.SWID);
        this.removeCookie(cookieConstants.TOKEN);
        this.removeCookie(cookieConstants.TOKEN_EXPIRATION);
        this.removeCookie(cookieConstants.TOKEN_START_TIME);
        this.removeCookie(cookieConstants.TOKEN_REFRESH);
        this.removeCookie(cookieConstants.TOKEN_REFRESH_PP);
        this.removeCookie(cookieConstants.GUEST_INFO);
        this.removeCookie(cookieConstants.JWT_TOKEN);
        this.removeCookie(cookieConstants.REMEMBER_ME);
        this.removeCookie(cookieConstants.CART_ID_MAPPING);
        this.removeCookie(cookieConstants.SESSION_TIMEOUT);
        this.removeCookie(cookieConstants.PHPSESSID);

        localStorage.removeItem(storageConstants.ADRUM_CLIENTINFO);

        this.cookieService.set(cookieConstants.TOKEN, access_token, this.sessionExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.TA_AUTH, access_token, this.sessionExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.SESSION_TTL, ttlString, this.sessionExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.TOKEN_EXPIRATION, expires_in, this.sessionExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.TOKEN_START_TIME, currentTime, this.sessionExpirationDate, path, domain);
    }

    /**
     * Stores agency info based cookies
     * @param data Agency info response
     * @param fromDTA flag to indicate whether or not is from DTA
     */
    storeAgencyInfo(data: AgencyLookup, fromDTA?: boolean): void {
        const cookieConstants = this.constants.COOKIES;
        const { path, domain } = cookieConstants.COOKIE_OPTIONS;
        const headerInfo = getValue(data, 'agencies.agency.agencyHeader');
        const addressInfo = getValue(data, 'agencies.agency.agencyAddresses.address[0]');
        const phoneInfo = getValue(data, 'agencies.agency.phones.phone[0]');
        const agencyId = headerInfo.agencyID;
        const guestSwid = `${this.cookieService.get(cookieConstants.CONVERSATION_UUID)}_${headerInfo.agencyID}`;
        const sessionGuid = data.msgHeader.sessionGuid;
        const taInfo = {
            id: agencyId,
            agencyName: headerInfo.name,
            zipCode: addressInfo.zip,
            country: addressInfo.country,
            city: addressInfo.city,
            addressLine1: addressInfo.address,
            addressLine2: '',
            state: addressInfo.state,
            phone: phoneInfo.phoneNumber,
            partnerIdType: this.constants.AFFILIATION_LABEL,
            agentId: data.agentId,
            lastName: data.agentLastName,
            firstName: data.agentFirstName,
            email: data.agentEmail,
            partnerId: headerInfo.clia || headerInfo.arc || headerInfo.iata,
            returnUrl: fromDTA ? this.getDTAReferrer() : ''
        };
        const taInfoString = JSON.stringify(taInfo);

        this.cookieService.set(cookieConstants.TA_SWID, guestSwid, this.oneDayExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.AGENCY_ID, agencyId, this.oneDayExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.TICKET_DATA, taInfoString, this.oneDayExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.SESSION_GUID, sessionGuid, this.sessionExpirationDate, path, domain);
    }

    /**
     * Stores SESSION_GUID cookie
     * @param data Agency info response
     */
    storeSessionGuid(data: AgencyLookup): void {
        const cookieConstants = this.constants.COOKIES;
        const { path, domain } = cookieConstants.COOKIE_OPTIONS;
        const sessionGuid = data && data.msgHeader && data.msgHeader.sessionGuid;

        this.cookieService.delete(cookieConstants.SESSION_GUID, path, domain);
        this.cookieService.set(cookieConstants.SESSION_GUID, sessionGuid, this.sessionExpirationDate, path, domain);
    }

    /**
     * Stores agency passport cookie
     * @param data passport token response
     * @param { string } data.token passport token
     */
    storeAgencyPassportToken({ token }: AgencyPassport): void {
        const { path, domain } = this.constants.COOKIES.COOKIE_OPTIONS;

        this.cookieService.set(this.constants.COOKIES.TA_INFO, token, this.oneDayExpirationDate, path, domain);
    }

    /**
     * Retrieve passport token request payload based on agency information
     * @param agencyInfo agency info response
     * @returns agency passport token request data
     */
    getPassportTokenRequestData(agencyInfo: AgencyLookup): AgencyPassportRequest {
        const agencyHeader = getValue(agencyInfo, 'agencies.agency.agencyHeader');
        const phoneNumber = getValue(agencyInfo, 'agencies.agency.phones.phone[0].phoneNumber');
        const affiliationId = agencyHeader && (agencyHeader.clia || agencyHeader.arc || agencyHeader.iata);

        return <AgencyPassportRequest> {
            phoneNumber,
            affiliationId,
            reservationId: this.constants.DEFAULT_RESERVATION_ID
        };
    }

    /**
     * Clear all travel agent data
     */
    clearTravelAgentData(): void {
        const cookieConstants = this.constants.COOKIES;
        const { path, domain } = cookieConstants.COOKIE_OPTIONS;

        this.cookieService.delete(cookieConstants.TOKEN, path, domain);
        this.cookieService.delete(cookieConstants.TOKEN_EXPIRATION, path, domain);
        this.cookieService.delete(cookieConstants.TOKEN_START_TIME, path, domain);
        this.cookieService.delete(cookieConstants.SESSION_TTL, path, domain);
        this.cookieService.delete(cookieConstants.TA_AUTH, path, domain);
        this.cookieService.delete(cookieConstants.AGENCY_ID, path, domain);
        this.cookieService.delete(cookieConstants.SESSION_GUID, path, domain);
        this.cookieService.delete(cookieConstants.TICKET_DATA, path, domain);
        this.cookieService.delete(cookieConstants.TA_SWID, path, domain);
        this.cookieService.delete(cookieConstants.TA_INFO, path, domain);
        localStorage.removeItem(this.constants.STORAGE.RECENT_RESERVATIONS);
        localStorage.removeItem(this.constants.STORAGE.ACTIVE_SESSION);
        sessionStorage.removeItem(this.constants.STORAGE.GUEST_CASTAWAY_INFO);
    }

    /**
     * Clear DTA referrer from storage
     */
    removeDTAReferrer(): void {
        localStorage.removeItem(this.constants.STORAGE.DTA_REFERRER);
    }

    /**
     * Return DTA referrer from storage
     * @returns DTA referrer
     */
    getDTAReferrer(): string {
        return localStorage.getItem(this.constants.STORAGE.DTA_REFERRER);
    }

    /**
     * Store DTA referrer in local storage
     */
    storeDTAReferrer(): void {
        if (!this.getDTAReferrer()) {
            localStorage.setItem(this.constants.STORAGE.DTA_REFERRER, document.referrer);
        }
    }

    /**
     * Method to redirect to DTA site
     */
    redirectToDTAReferrer(): void {
        const referrer = this.getDTAReferrer();

        this.removeDTAReferrer();
        this.windowRef.nativeWindow.location.href = referrer;
    }

    /**
     * Login based on agency data
     * @param data agency login data
     * @param data.phoneNumber agency phone number
     * @param data.affiliationId agency affiliation id
     * @returns Observable for agency login process
     */
    agencyLogin({ phoneNumber, affiliationId }: AgencyLogin): Observable<null> {
        return this.travelAgentRestService.getAuthToken()
            .pipe(
                tap((resp: AgencyAuthToken) => this.storeAuthToken(resp)),
                concatMap(() =>
                    forkJoin([
                        this.travelAgentRestService.agencyLookup({ phoneNumber, affiliationId })
                            .pipe(tap((resp: AgencyLookup) => this.storeAgencyInfo(resp))),
                        this.travelAgentRestService.getPassportToken(<AgencyPassportRequest> {
                                phoneNumber,
                                affiliationId,
                                reservationId: this.constants.DEFAULT_RESERVATION_ID
                            })
                            .pipe(tap((resp: AgencyPassport) => this.storeAgencyPassportToken(resp)))
                    ])
                ),
                catchError(error => {
                    this.clearTravelAgentData();

                    return _throw(error);
                }),
                concatMap(resp => empty())
            );
    }

    /**
     * Login based on DTA token
     * @param tokens DTA tokens
     * @returns Observable for agency login process
     */
    dtaLogin({ tokens }: AgencyLogin): Observable<null> {
        this.storeDTAReferrer();

        return this.travelAgentRestService.getAuthToken()
            .pipe(
                tap((resp: AgencyAuthToken) => this.storeAuthToken(resp)),
                concatMap(() => this.travelAgentRestService.agencyLookup({ tokens })),
                tap((resp: AgencyLookup) => this.storeAgencyInfo(resp, true)),
                concatMap((resp: AgencyLookup) => {
                    return this.travelAgentRestService.getPassportToken(this.getPassportTokenRequestData(resp));
                }),
                tap((resp: AgencyPassport) => this.storeAgencyPassportToken(resp)),
                catchError(error => {
                    this.clearTravelAgentData();

                    return _throw(error);
                }),
                concatMap(resp => empty())
            );
    }

    /**
     * Delete sessions and agency data
     * @returns Observable for agency login process
     */
    logout(): Observable<never> {
        let sessions: Array<string>;
        let observable: Observable<never>;

        try {
            sessions = JSON.parse(localStorage.getItem(this.constants.STORAGE.ACTIVE_SESSION));
        } catch (error) {
            sessions = null;
        }

        observable = sessions ? this.travelAgentRestService.agencyLogout(<AgencyLogout> { sessions }) : empty();

        return observable.pipe(finalize(() => this.clearTravelAgentData()));
    }

    /**
     * Stores guest castaway info in session storage
     * @param data castaway info
     */
    storeGuestCastawayData(data: GuestCastawayInfo): void {
        try {
            sessionStorage.setItem(
                this.constants.STORAGE.GUEST_CASTAWAY_INFO,
                encodeURIComponent(JSON.stringify(data))
            );
        } catch (error) {}
    }

    /**
     * Retrieve stored guest castaway data
     * @return guest castaway data from session storage
     */
    getGuestCastawayData(): GuestCastawayInfo {
        let guestData: GuestCastawayInfo;

        try {
            guestData = JSON.parse(
                decodeURIComponent(sessionStorage.getItem(this.constants.STORAGE.GUEST_CASTAWAY_INFO))
            );
        } catch (error) {}

        return guestData;
    }

    /**
     * Deletes stored guest castaway data
     */
    deleteGuestCastawayData(): void {
        sessionStorage.removeItem(this.constants.STORAGE.GUEST_CASTAWAY_INFO);
    }
}
